/**
 * Knack stat bars: counts each bar's number up from 0 as it scrolls into view, in sync with the
 * bar's CSS width transition (see sections/knack-stat-bars.liquid). Each bar animates once, the
 * first time it enters the viewport.
 *
 * Progressive enhancement: with no JS the bars and numbers show their final value immediately (the
 * CSS default). Under prefers-reduced-motion this script does nothing, matching the CSS, which also
 * skips the fill transition under reduced motion.
 */
const COUNT_DURATION = 900;

class KnackStatBars extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.bars = [...this.querySelectorAll('.knack-statbars__bar')];
    if (!this.bars.length) return;

    this.bars.forEach((bar) => {
      const target = parseFloat(bar.getAttribute('data-knack-stat-value'));
      if (!Number.isFinite(target)) return;
      const suffix = bar.getAttribute('data-knack-stat-suffix') || '';
      bar.querySelectorAll('.knack-statbars__value').forEach((el) => {
        el.textContent = '0' + suffix;
      });
    });

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.countUp(entry.target);
          this.observer.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );
    this.bars.forEach((bar) => this.observer.observe(bar));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  countUp(bar) {
    const target = parseFloat(bar.getAttribute('data-knack-stat-value'));
    if (!Number.isFinite(target)) return;
    const suffix = bar.getAttribute('data-knack-stat-suffix') || '';
    const valueEls = [...bar.querySelectorAll('.knack-statbars__value')];
    const start = performance.now();

    bar.classList.add('is-counted');

    const tick = (now) => {
      const progress = Math.min((now - start) / COUNT_DURATION, 1);
      const eased = 1 - (1 - progress) ** 3;
      const current = Math.round(target * eased);
      valueEls.forEach((el) => {
        el.textContent = current + suffix;
      });
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
}

if (!customElements.get('knack-stat-bars')) {
  customElements.define('knack-stat-bars', KnackStatBars);
}
